import { useState } from "react";
import { toast } from "sonner";
import { Copy, MessageSquare, Mail, Check } from "lucide-react";

const PROPERTIES = [
  ["de-la-vie", "De La Vie Maison"],
  ["heritage", "Heritage"],
  ["hearthstead", "Hearthstead Cottage"],
];

const inputCls =
  "w-full bg-transparent border-b border-[#f7f2e7]/30 focus:border-[#f7f2e7] outline-none py-2.5 text-sm text-[#f7f2e7] placeholder:text-[#f7f2e7]/35 transition-colors duration-300";
const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60 mb-1";

export default function ReviewInvite() {
  const [property, setProperty] = useState("de-la-vie");
  const [guest, setGuest] = useState("");
  const [copied, setCopied] = useState(false);

  const params = new URLSearchParams({ residence: property });
  if (guest.trim()) params.set("name", guest.trim());
  const link = `${window.location.origin}/review?${params.toString()}`;
  const residence = PROPERTIES.find((p) => p[0] === property)[1];
  const first = guest.trim().split(" ")[0];
  const message =
    `Hi${first ? ` ${first}` : ""}! Thank you so much for staying at ${residence}. ` +
    `It was a pleasure hosting you. If you have a moment, we'd love a few words about your stay — it takes under a minute: ${link} ` +
    `Safe travels, and we hope to welcome you back! — Parker Furnished Stays`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Message copied — paste it into a text or email.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select the text and copy it manually.");
    }
  };

  const btn =
    "inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] px-4 py-3.5 transition-colors duration-300";

  return (
    <div data-testid="review-invite" className="mt-8 border-t border-[#f7f2e7]/10 pt-7 space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60">Invite a guest to review</p>
        <p className="text-xs text-[#f7f2e7]/60 font-light mt-2">
          Pick the residence, add the guest's name, then copy or send the ready-made message at checkout.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label htmlFor="inv-property" className={labelCls}>Residence</label>
          <select
            id="inv-property"
            data-testid="invite-property-select"
            value={property}
            onChange={(e) => setProperty(e.target.value)}
            className={`${inputCls} cursor-pointer bg-[#1a3324]`}
          >
            {PROPERTIES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="inv-guest" className={labelCls}>Guest name (optional)</label>
          <input
            id="inv-guest"
            data-testid="invite-guest-input"
            value={guest}
            onChange={(e) => setGuest(e.target.value)}
            maxLength={80}
            placeholder="e.g. Amanda"
            className={inputCls}
          />
        </div>
      </div>
      <div data-testid="invite-message" className="border border-[#f7f2e7]/15 bg-[#16301f] p-4 text-sm text-[#f7f2e7]/85 font-light leading-relaxed whitespace-pre-wrap break-words">
        {message}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <button
          data-testid="invite-copy-btn"
          type="button"
          onClick={copy}
          className={`${btn} bg-[#f7f2e7] text-[#16301f] hover:bg-[#c9a227]`}
        >
          {copied ? <Check className="w-4 h-4" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <a
          data-testid="invite-sms-link"
          href={`sms:?&body=${encodeURIComponent(message)}`}
          className={`${btn} border border-[#f7f2e7]/30 text-[#f7f2e7] hover:border-[#f7f2e7]`}
        >
          <MessageSquare className="w-4 h-4" strokeWidth={1.5} /> Text
        </a>
        <a
          data-testid="invite-email-link"
          href={`mailto:?subject=${encodeURIComponent(`Thank you for staying at ${residence}`)}&body=${encodeURIComponent(message)}`}
          className={`${btn} border border-[#f7f2e7]/30 text-[#f7f2e7] hover:border-[#f7f2e7]`}
        >
          <Mail className="w-4 h-4" strokeWidth={1.5} /> Email
        </a>
      </div>
    </div>
  );
}
